import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfGroup } from 'tio-data/data/resources/pslf-group.type';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type FeaturesService from 'tio-common/services/features';
import type PslfDebugService from 'tio-employee/services/pslf-debug';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import { getLoanSummaryRequest } from 'tio-data/requests/user';
import { findRecord, query } from '@warp-drive/utilities/json-api';

export default class AuthenticatedPslfDashboardIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare pslfDebug: PslfDebugService;

  async model() {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id;
    if (!personId) {
      throw new Error('Cannot load the PSLF dashboard without a person id.');
    }
    const parentModel = this.modelFor('authenticated.pslf') as {
      pslfProfile: PslfProfileModel;
      groups: PslfGroup[];
    };

    const pslfProfile = parentModel.pslfProfile;

    const [loanSummaryResp, personResp, legacyPslfRecResp, pslf0726RecResp, pslfFormsResp] =
      await Promise.all([
        userId
          ? this.store.request(getLoanSummaryRequest(userId))
          : Promise.resolve({ content: undefined as unknown }),
        this.store.request(
          findRecord<PersonModel>('person', personId, {
            include:
              'accounts.financial-institution,accounts.loans,email-addresses,phone-numbers,postal-addresses',
            reload: true,
          })
        ) as Promise<{ content: { data: PersonModel } }>,
        this.store.request(
          query<RecommendationModel>('recommendation', {
            'filter[user]': userId,
            'filter[requesting-app]': 'PSLF',
            include: 'user',
            sort: '-created-at',
            'page[offset]': 0,
            'page[limit]': 10,
          })
        ) as Promise<{ content: { data: RecommendationModel[] } }>,
        // PSLF-0726 runs — surfaced (with the legacy ones) in the dashboard
        // history table.
        this.store.request(
          query<RecommendationModel>(
            'recommendation',
            {
              'filter[user]': userId,
              'filter[requesting-app]': 'PSLF-0726',
              include: 'user',
              sort: '-created-at',
              'page[offset]': 0,
              'page[limit]': 10,
            },
            // Force a fresh fetch: the estimator's didSave transitions back here
            // right after creating a recommendation, and the default CachePolicy
            // would otherwise serve a stale cached result (missing the new run).
            { reload: true }
          )
        ) as Promise<{ content: { data: RecommendationModel[] } }>,
        // Fresh fetch so a form submitted moments ago shows up, with the profile whose annual
        // fields it changed: the parent route loads that only once.
        this.store.request(
          query<PslfFormModel>(
            'pslf-form',
            {
              'filter[pslf-profile]': pslfProfile.id,
              include: 'attachments,pslf-profile',
            },
            { reload: true }
          )
        ) as Promise<{ content: { data: PslfFormModel[] } }>,
      ]);

    const person = personResp.content.data;
    const legacyPslfRecs = legacyPslfRecResp.content.data ?? [];
    const pslf0726Recs = pslf0726RecResp.content.data ?? [];
    const pslfForms = pslfFormsResp.content.data;

    // Combined history (PSLF-0726 + legacy PSLF), newest first — mirrors the IDR
    // dashboard.
    // Compare parsed instants, not strings — serialized offsets flip across
    // DST (-05:00/-06:00) within one list, breaking lexicographic order.
    const history = [...pslf0726Recs, ...legacyPslfRecs]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .slice(0, 10);

    const baseModel = {
      accounts: person.accounts,
      loanSummary: loanSummaryResp.content as LoanSummary,
      // Unchanged hero behavior: the legacy "View Estimate" uses the most recent
      // legacy PSLF recommendation.
      mostRecentRecommendation: legacyPslfRecs[0] as RecommendationModel | undefined,
      history,
      person: person,
      pslfProfile: pslfProfile,
      pslfForms: pslfForms.slice() as PslfFormModel[],
      debugLinkLoans: false,
    };

    // Apply PSLF debug overrides (feature-flagged; dead code in production)
    if (this.features.isEnabled('pslfDebugPanel') && this.pslfDebug.isActive()) {
      return this.pslfDebug.applyTo(baseModel);
    }

    return baseModel;
  }
}
